import { PartyRoleValues, type PartyRole } from '@/api/generated/enums';
import type { Party, PartyDuplicate } from '@/api/masters';

/**
 * What an identity is used for, in the words the shop uses (the party merge, phase 4).
 *
 * <p>
 * <strong>Its own module because the enum it maps is generated</strong>, exactly as
 * `pages/vendors/capabilities.ts` and `pages/employees/capabilities.ts` are. A fourth role added on
 * the server would otherwise arrive with no label and render an empty chip against a party that is
 * plainly something. Two guards, in that order: the `Record<PartyRole, string>` below makes it a
 * compile error, and `everyRoleHasALabel` covers what the type cannot, since an empty string
 * satisfies the type and renders nothing.
 * </p>
 *
 * <p>
 * The words are the master's own, not the enum's: an operator looking at this list thinks in terms
 * of the three screens they already know.
 * </p>
 */
export const roleLabels: Record<PartyRole, string> = {
  Customer: 'Customer',
  Vendor: 'Vendor',
  Employee: 'Staff',
};

/**
 * The chips above the list. `undefined` is "everyone", which is not a role and deliberately not a
 * value the API is sent.
 */
export const roleFilters: { value: PartyRole | undefined; label: string }[] = [
  { value: undefined, label: 'Everyone' },
  ...PartyRoleValues.map((value) => ({ value, label: roleLabels[value] })),
];

/**
 * What a party is, as one readable phrase — "Customer and Staff".
 *
 * <p>
 * An identity with no live role gets a sentence rather than an empty space. It is a real state: the
 * only record using it has been deleted, and the party still holds the number until something reuses
 * it. Rendering nothing there would read as a loading bug.
 * </p>
 */
export function describeRoles(roles: PartyRole[]): string {
  if (roles.length === 0) {
    return 'No live record';
  }

  const words = roles.map((role) => roleLabels[role]);

  return words.length === 1
    ? words[0]!
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

export function everyRoleHasALabel(): boolean {
  return PartyRoleValues.every((role) => Boolean(roleLabels[role]));
}

/**
 * Whether these two identities can be folded together.
 *
 * <p>
 * <strong>The client refuses what the server refuses, before the operator commits to it.</strong>
 * Two customer rows on one identity is not a merged person, it is a duplicate customer — a different
 * problem with a different fix. The server answers 409; saying it here means the Merge button is
 * disabled with the reason beside it rather than the operator discovering it after choosing.
 * </p>
 */
export function mergeProblems(keep: Party, absorb: Party): string[] {
  if (keep.id === absorb.id) {
    return ['Pick two different records.'];
  }

  const clash = keep.roles.filter((role) => absorb.roles.includes(role));

  if (clash.length > 0) {
    const both = clash.map((role) => roleLabels[role].toLowerCase()).join(' and ');

    return [
      `Both records are a ${both}, so merging them would leave one person with two. ` +
        `That is a duplicate to resolve on the ${both} screen first.`,
    ];
  }

  return [];
}

/**
 * The identity to keep, suggested rather than chosen.
 *
 * <p>
 * The one holding more roles, because it is the one more things already point at — and on a tie, the
 * older id, since that is the record the shop has had longer. It is only a default: the operator
 * picks, and the surviving party keeps its own name and number, so which one survives is a decision
 * about whose spelling is right.
 * </p>
 */
export function suggestKeeper(parties: Party[]): Party | undefined {
  return [...parties].sort((a, b) => b.roles.length - a.roles.length || a.id - b.id)[0];
}

/**
 * How much work a shop has left, as one sentence for the notice above the list.
 *
 * Counted in records rather than groups: "four records across two numbers" is what an operator is
 * about to do, where "two duplicates" understates it.
 */
export function outstandingSummary(duplicates: PartyDuplicate[]): string | null {
  if (duplicates.length === 0) {
    return null;
  }

  const records = duplicates.reduce((total, group) => total + group.parties.length, 0);

  return (
    `${records} records across ${duplicates.length} ` +
    `${duplicates.length === 1 ? 'number' : 'numbers'} are the same person more than once.`
  );
}
